import { Account } from '../AccountCard';
import { CalendarProvider, refactorCalendarName } from '../../utils/common';

export interface CalendarSelectGroup {
    group: string;
    items: { value: string; label: string; disabled?: boolean }[];
}

function collectPrimaryPlatformIds(accounts: Account[]): string[] {
    const primaryCalendars: string[] = [];
    for (const account of accounts) {
        for (const calendar of account.calendars ?? []) {
            if (calendar.primary) {
                primaryCalendars.push(calendar.platform_id);
            }
        }
    }
    return primaryCalendars;
}

export function calendarUUIDToProvider(accounts: Account[], calendarUUID: string): CalendarProvider | undefined {
    for (const account of accounts) {
        for (const calendar of account.calendars ?? []) {
            if (calendar.uuid === calendarUUID) {
                return account.provider;
            }
        }
    }
    return undefined;
}

export function buildSourceCalendarOptions(accounts: Account[]): CalendarSelectGroup[] {
    if (!accounts?.length) {
        return [];
    }
    const primaryCalendars = collectPrimaryPlatformIds(accounts);
    return accounts.map(a => ({
        group: `${a.key} | ${a.provider.charAt(0).toUpperCase() + a.provider.slice(1)}`,
        items: (
            a.calendars?.filter(
                c => c.primary || !primaryCalendars.includes(c.platform_id)
            ).map(
                c => ({ value: c.uuid, label: refactorCalendarName(c.name) })
            ) || []
        ),
    }));
}

export function buildDestinationCalendarOptions(
    accounts: Account[],
    t: (key: string) => string,
    excludeCalendarUuid?: string,
): CalendarSelectGroup[] {
    if (!accounts?.length) {
        return [];
    }
    return accounts.map(a => ({
        group: `${a.key} | ${a.provider.charAt(0).toUpperCase() + a.provider.slice(1)}`,
        items: a.calendars?.filter(c => !c.readonly && c.uuid !== excludeCalendarUuid).map(c => ({
            value: c.uuid,
            label: a.provider === CalendarProvider.ICS
                ? `${refactorCalendarName(c.name)} (${t('dashboard.sync.ics-cannot-be-destination')})`
                : refactorCalendarName(c.name),
            disabled: a.provider === CalendarProvider.ICS,
        })) || [],
    }));
}

export function countEligibleDestinations(
    accounts: Account[],
    excludeCalendarUuid?: string,
): number {
    return buildDestinationCalendarOptions(accounts, () => '', excludeCalendarUuid)
        .flatMap(g => g.items)
        .filter(item => !item.disabled)
        .length;
}

export function findPrimaryCalendarUuid(accounts: Account[]): string | null {
    for (const account of accounts) {
        for (const calendar of account.calendars ?? []) {
            if (calendar.primary) {
                return calendar.uuid;
            }
        }
    }
    const first = accounts[0]?.calendars?.[0];
    return first?.uuid ?? null;
}

export function getCalendarLabel(accounts: Account[], calendarUuid: string): string {
    for (const account of accounts) {
        for (const calendar of account.calendars ?? []) {
            if (calendar.uuid === calendarUuid) {
                return `${refactorCalendarName(calendar.name)} (${account.key})`;
            }
        }
    }
    return calendarUuid;
}
